import { useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { PageHeader } from "../components/ui/page-header";
import { ExpenseForm, type ExpenseFormSubmit } from "../components/ExpenseForm";
import {
  RecurringScheduleFields,
  toDatetimeLocalHourly,
  toUtcHourlyIso,
  type RecurringScheduleValue,
} from "../components/RecurringScheduleFields";
import {
  useGroup,
  useRecurringExpense,
  useUpdateRecurringExpense,
} from "../api/hooks";
import type {
  Expense,
  Group,
  RecurringExpense,
  UpdateRecurringExpenseRequest,
} from "../api/types";
import { itemsEqualByContent, paymentsEqual } from "../lib/expenseDiff";

export function EditRecurringExpensePage(): ReactNode {
  const { groupId, recurringExpenseId } = useParams<{
    groupId: string;
    recurringExpenseId: string;
  }>();
  const groupQ = useGroup(groupId);
  const recurringQ = useRecurringExpense(groupId, recurringExpenseId);

  if (groupQ.isLoading || recurringQ.isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-stack-lg">
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (!groupQ.data || !recurringQ.data) {
    return (
      <Card tone="ghost">
        <CardContent className="py-12 text-center text-on-surface-variant">
          Recurring expense not found.
        </CardContent>
      </Card>
    );
  }
  return <EditRecurring group={groupQ.data} recurring={recurringQ.data} />;
}

function EditRecurring({
  group,
  recurring,
}: {
  group: Group;
  recurring: RecurringExpense;
}): ReactNode {
  const navigate = useNavigate();
  const update = useUpdateRecurringExpense(group.id);
  const groupPath = `/groups/${group.id}`;

  const initialSchedule = useMemo<RecurringScheduleValue>(
    () => ({
      unit: recurring.unit,
      intervalCount: recurring.intervalCount,
      // `<input datetime-local>` accepts a `YYYY-MM-DDTHH:00` value in the
      // viewer's local timezone. `new Date(utcIso)` parses the UTC instant;
      // the helper formats it using browser-local fields, so the editor
      // round-trips identically to what `toUtcHourlyIso` will produce on
      // submit.
      nextRunLocal: toDatetimeLocalHourly(new Date(recurring.nextRunAt)),
      status: recurring.status,
    }),
    [recurring],
  );

  const [schedule, setSchedule] =
    useState<RecurringScheduleValue>(initialSchedule);

  // The recurring template doesn't carry per-item ids (items are flattened
  // when materialised). Build a synthetic Expense-shaped seed for ExpenseForm.
  const expenseSeed: Expense = useMemo(
    () => ({
      id: recurring.id,
      groupId: recurring.groupId,
      label: recurring.label,
      currency: recurring.currency,
      payments: recurring.payments,
      items: recurring.items.map((it, idx) => ({
        id: `__synthetic_${idx}`,
        label: it.label,
        cost: it.cost,
        split: it.split,
      })),
      comments: [],
      expenseDate: recurring.nextRunAt.slice(0, 10),
      createdAt: recurring.createdAt,
      updatedAt: recurring.updatedAt,
      groupCharge: recurring.payments.reduce((s, p) => s + p.charge, 0),
      // Synthetic seed: recurring templates have no concurrency token and are
      // saved through the recurring-expense update path, never this expense's.
      version: 1,
    }),
    [recurring],
  );

  const utcIso = toUtcHourlyIso(schedule.nextRunLocal);
  const formKey = `${recurring.updatedAt}|${group.participants
    .map((p) => p.id)
    .sort()
    .join("|")}`;

  async function onSubmit(values: ExpenseFormSubmit): Promise<void> {
    const body: UpdateRecurringExpenseRequest = {};
    if (values.label !== recurring.label) body.label = values.label;
    if (!paymentsEqual(values.payments, recurring.payments)) {
      body.payments = values.payments;
    }
    const itemsForApi = values.items.map((it) => ({
      label: it.label,
      cost: it.cost,
      split: it.split,
    }));
    if (!itemsEqualByContent(itemsForApi, recurring.items)) {
      body.items = itemsForApi;
    }
    if (schedule.unit !== recurring.unit) body.unit = schedule.unit;
    if (schedule.intervalCount !== recurring.intervalCount) {
      body.intervalCount = schedule.intervalCount;
    }
    if (utcIso && utcIso !== recurring.nextRunAt) body.nextRunAt = utcIso;
    if (schedule.status && schedule.status !== recurring.status) {
      body.status = schedule.status;
    }

    if (Object.keys(body).length === 0) {
      navigate(groupPath);
      return;
    }

    await update.mutateAsync({
      recurringExpenseId: recurring.id,
      body,
    });
    navigate(groupPath);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-stack-lg">
      <PageHeader
        back={{ label: "Back", onClick: () => navigate(groupPath) }}
        title="Edit recurring expense"
        description="Pause, retime, or change which items are created on each run."
      />
      <ExpenseForm
        key={formKey}
        group={group}
        expense={expenseSeed}
        showExpenseDate={false}
        submitLabel="Save changes"
        pendingLabel="Saving…"
        submitting={update.isPending}
        serverError={update.error ? "Couldn't save changes." : null}
        extraValid={utcIso !== null && schedule.intervalCount >= 1}
        extraFields={
          <RecurringScheduleFields
            value={schedule}
            onChange={setSchedule}
            showStatusToggle
          />
        }
        onCancel={() => navigate(groupPath)}
        onSubmit={onSubmit}
      />
    </div>
  );
}
